import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setSideTab } from "../../../toolkit/slices/inventories/inventorySlice";
import accessCheckPromise from "../../../../utils/accessCheck";
import { logoutUser } from "../../../toolkit/slices/users/userSlice";
import AuthorizationError from "../AuthorizationError";
import { useSelector } from "react-redux";
import {
  Card,
  Row,
  Col,
  Image,
  Button,
  Typography,
  Space,
  Tag,
  Spin,
  Empty,
  message,
  Tooltip,
} from "antd";
import {
  DownloadOutlined,
  EnvironmentOutlined,
  FileImageOutlined,
  CloseOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import moment from "moment";

const { Title, Text } = Typography;

const MountingProofContainer = () => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const userId = useSelector((state) => state.user._id);
  const dispatch = useDispatch();
  const [access, setAccess] = useState(false);
  const [proofs, setProofs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageDetails, setSelectedImageDetails] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [allImages, setAllImages] = useState([]);

  useEffect(() => {
    dispatch(setSideTab(window.location.pathname));
    accessCheckPromise()
      .then((res) => setAccess(res))
      .catch((error) => {
        dispatch(logoutUser());
      });
  }, []);

  useEffect(() => {
    if (access) {
      fetchProofs();
    }
  }, [access]);

  const fetchProofs = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/getProofs?userId=${userId}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();
      if (result.success) {
        setProofs(result.data);
      } else {
        message.error("Failed to fetch proofs");
      }
    } catch (error) {
      console.error("Error fetching proofs:", error);
      message.error("Error fetching proofs");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadImage = (imageUrl, fileName) => {
    try {
      // Create a temporary link element
      const link = document.createElement("a");
      link.href = imageUrl;
      link.download = fileName || "field-proof-image.jpg";
      link.target = "_blank";

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      //   message.success("Image download started");
    } catch (error) {
      console.error("Error downloading image:", error);
      message.error("Failed to download image. Please try again.");
    }
  };

  const showImageModal = (image, proof, imageIndex) => {
    setSelectedImage(image);
    setSelectedImageDetails(proof);
    setCurrentImageIndex(imageIndex);
    setAllImages(proof.images);
    setImageModalVisible(true);
  };

  const closeImageModal = () => {
    setImageModalVisible(false);
    setSelectedImage(null);
    setSelectedImageDetails(null);
    setCurrentImageIndex(0);
    setAllImages([]);
  };

  const goToNextImage = () => {
    if (currentImageIndex < allImages.length - 1) {
      const nextIndex = currentImageIndex + 1;
      setCurrentImageIndex(nextIndex);
      setSelectedImage(allImages[nextIndex]);
    }
  };

  const goToPreviousImage = () => {
    if (currentImageIndex > 0) {
      const prevIndex = currentImageIndex - 1;
      setCurrentImageIndex(prevIndex);
      setSelectedImage(allImages[prevIndex]);
    }
  };

  const formatDate = (dateString) => {
    try {
      return moment(dateString).format("DD-MM-YYYY hh:mm A");
    } catch (error) {
      return dateString;
    }
  };

  if (!access) {
    return <AuthorizationError />;
  }

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div
      className="parent-padding-container"
      style={{
        backgroundColor: "white",
        borderRadius: "12px",
        padding: "20px",
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          padding: "0px 20px",
          borderRadius: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "1rem",
          marginBottom: "24px",
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          Mounting Proof
        </Title>
        <Text type="secondary">Total Proofs: {proofs.length}</Text>
      </div>

      {proofs.length === 0 ? (
        <Empty
          description="No mounting proofs found"
          style={{ marginTop: "50px" }}
        />
      ) : (
        <Row gutter={[16, 16]}>
          {proofs.map((proof, index) => (
            <Col xs={24} lg={12} xl={8} key={proof._id}>
              <Card hoverable style={{ height: "100%" }}>
                <div style={{ marginBottom: "16px" }}>
                  <Space
                    direction="vertical"
                    size="small"
                    style={{ width: "100%" }}
                  >
                    {/* Sales Person Info */}
                    <Tooltip
                      title={`Sales Person: ${proof.salesPersonId.name} (${proof.salesPersonId.email})`}
                    >
                      <div
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          cursor: "pointer",
                        }}
                      >
                        <Text strong>Sales Person:</Text>{" "}
                        <Text>{proof.salesPersonId.name}</Text>{" "}
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          ({proof.salesPersonId.email})
                        </Text>
                      </div>
                    </Tooltip>

                    {/* Mounter Info */}
                    <Tooltip
                      title={`Mounter: ${proof.mounterId.name} (${proof.mounterId.email})`}
                    >
                      <div
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          cursor: "pointer",
                        }}
                      >
                        <Text strong>Mounter:</Text>{" "}
                        <Text>{proof.mounterId.name}</Text>{" "}
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          ({proof.mounterId.email})
                        </Text>
                      </div>
                    </Tooltip>

                    {/* Date Info */}
                    <div>
                      <Text strong>Sent on:</Text>{" "}
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        {formatDate(proof.createdAt)}
                      </Text>
                    </div>

                    {/* Campaign Info */}
                    <div>
                      <Text strong>Campaign Name:</Text>{" "}
                      <Tag color="blue">{proof.campaignName}</Tag>
                    </div>

                    {/* Images Section */}
                    <div>
                      <Text strong>Images ({proof.images.length}):</Text>
                      <div style={{ marginTop: "8px" }}>
                        <Row gutter={[8, 8]}>
                          {proof.images.slice(0, 2).map((image, imgIndex) => (
                            <Col span={12} key={image._id}>
                              <div style={{ position: "relative" }}>
                                <Image
                                  src={image.image}
                                  alt={`Proof ${index + 1} - Image ${
                                    imgIndex + 1
                                  }`}
                                  style={{
                                    width: "100%",
                                    height: "80px",
                                    objectFit: "cover",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    showImageModal(image, proof, imgIndex)
                                  }
                                  preview={false}
                                />
                                <Button
                                  type="primary"
                                  size="small"
                                  icon={<DownloadOutlined />}
                                  style={{
                                    position: "absolute",
                                    top: "4px",
                                    right: "4px",
                                    minWidth: "auto",
                                    height: "24px",
                                    width: "24px",
                                    borderRadius: "50%",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownloadImage(
                                      image.image,
                                      `${proof.campaignName}_proof_${
                                        index + 1
                                      }_image_${imgIndex + 1}.jpg`
                                    );
                                  }}
                                />
                              </div>

                              {/* Location Info */}
                              {image.location && (
                                <div style={{ marginTop: "4px" }}>
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: "10px" }}
                                  >
                                    <EnvironmentOutlined
                                      style={{ marginRight: "2px" }}
                                    />
                                    {image.location}
                                  </Text>
                                </div>
                              )}
                            </Col>
                          ))}
                          {proof.images.length > 2 && (
                            <Col span={24}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  height: "80px",
                                  backgroundColor: "#f5f5f5",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  showImageModal(proof.images[0], proof, 0)
                                }
                              >
                                <Text type="secondary">
                                  +{proof.images.length - 2} more images...
                                </Text>
                              </div>
                            </Col>
                          )}
                        </Row>
                      </div>
                    </div>
                  </Space>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: imageModalVisible ? "flex" : "none",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            padding: "20px",
          }}
          onClick={closeImageModal}
        >
          <div
            style={{
              position: "relative",
              maxWidth: "90%",
              maxHeight: "90%",
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "20px",
              maxWidth: "800px",
              width: "100%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <Title level={4} style={{ margin: 0 }}>
                {/* <FileImageOutlined style={{ marginRight: "8px" }} /> */}
                Image Details
              </Title>
              <div style={{ display: "flex", gap: "8px" }}>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownloadImage(
                      selectedImage.image,
                      "field-proof-image.jpg"
                    );
                  }}
                >
                  Download
                </Button>
                <Button
                  type="default"
                  icon={<CloseOutlined />}
                  onClick={closeImageModal}
                >
                  Close
                </Button>
              </div>
            </div>

            <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
              <div
                style={{ flex: "1", minWidth: "300px", position: "relative" }}
              >
                <Image
                  src={selectedImage.image}
                  alt="Full size image"
                  style={{
                    width: "100%",
                    maxHeight: "400px",
                    objectFit: "contain",
                    borderRadius: "8px",
                  }}
                  preview={false}
                />

                {/* Navigation Buttons */}
                {allImages.length > 1 && (
                  <>
                    {/* Previous Button */}
                    {currentImageIndex > 0 && (
                      <Button
                        type="primary"
                        shape="circle"
                        icon={<LeftOutlined />}
                        style={{
                          position: "absolute",
                          left: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          zIndex: 10,
                        }}
                        onClick={goToPreviousImage}
                      />
                    )}

                    {/* Next Button */}
                    {currentImageIndex < allImages.length - 1 && (
                      <Button
                        type="primary"
                        shape="circle"
                        icon={<RightOutlined />}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          zIndex: 10,
                        }}
                        onClick={goToNextImage}
                      />
                    )}

                    {/* Image Counter */}
                    <div
                      style={{
                        position: "absolute",
                        bottom: "10px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: "rgba(0, 0, 0, 0.7)",
                        color: "white",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                      }}
                    >
                      {currentImageIndex + 1} / {allImages.length}
                    </div>
                  </>
                )}
              </div>

              {selectedImageDetails && (
                <div style={{ flex: "1", minWidth: "300px" }}>
                  <Space
                    direction="vertical"
                    size="small"
                    style={{ width: "100%" }}
                  >
                    {/* Image Location Details */}
                    <div style={{ marginTop: "1rem" }}>
                      <Text strong style={{ fontSize: "16px" }}>
                        Location Details:
                      </Text>
                      <div style={{ marginTop: "8px" }}>
                        {selectedImage.location && (
                          <div style={{ marginBottom: "4px" }}>
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              <Text strong>Address:</Text>{" "}
                              {selectedImage.location}
                            </Text>
                          </div>
                        )}
                        {selectedImage.area && (
                          <div style={{ marginBottom: "4px" }}>
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              <Text strong>Area:</Text> {selectedImage.area}
                            </Text>
                          </div>
                        )}
                        {selectedImage.city && selectedImage.state && (
                          <div style={{ marginBottom: "4px" }}>
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              <Text strong>City & State:</Text>{" "}
                              {selectedImage.city}, {selectedImage.state}
                            </Text>
                          </div>
                        )}
                        {selectedImage.city && !selectedImage.state && (
                          <div style={{ marginBottom: "4px" }}>
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              <Text strong>City:</Text> {selectedImage.city}
                            </Text>
                          </div>
                        )}
                        {!selectedImage.city && selectedImage.state && (
                          <div style={{ marginBottom: "4px" }}>
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              <Text strong>State:</Text> {selectedImage.state}
                            </Text>
                          </div>
                        )}
                        {selectedImage.pinCode && (
                          <div style={{ marginBottom: "4px" }}>
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              <Text strong>PIN Code:</Text>{" "}
                              {selectedImage.pinCode}
                            </Text>
                          </div>
                        )}
                        {/* {selectedImage.lat && selectedImage.long && (
                          <div style={{ marginBottom: "4px" }}>
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              <Text strong>Coordinates:</Text>{" "}
                              {selectedImage.lat.toFixed(4)},{" "}
                              {selectedImage.long.toFixed(4)}
                            </Text>
                          </div>
                        )} */}
                      </div>
                    </div>
                  </Space>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MountingProofContainer;
